import api from './api';
import { buildQueryParams } from './utils/query-params';
import type {
  ApiResponse,
  PaginatedResponse,
  User,
  UserFilters,
  CreateUserData,
  UpdateUserData,
  LoginSuccessResponse,
} from '@/types';

// Get users with filtering and pagination
export const getUsers = async (filters: UserFilters = {}) => {
  const query = buildQueryParams(filters);
  const response = await api.get<PaginatedResponse<User>>(`/users?${query}`);
  return response.data;
};

// Get single user
export const getUser = async (id: string) => {
  const response = await api.get<ApiResponse<User>>(`/users/${id}`);
  return response.data.data;
};

// Create user
export const createUser = async (data: CreateUserData) => {
  const response = await api.post<ApiResponse<User>>('/users', data);
  return response.data.data;
};

// Update user
export const updateUser = async (id: string, data: UpdateUserData) => {
  const response = await api.put<ApiResponse<User>>(`/users/${id}`, data);
  return response.data.data;
};

// Delete user
export const deleteUser = async (id: string) => {
  await api.delete(`/users/${id}`);
};

// Toggle user active status
export const toggleUserStatus = async (id: string, isActive: boolean) => {
  const response = await api.put<ApiResponse<User>>(`/users/${id}`, { is_active: isActive });
  return response.data.data;
};

// Impersonate a user (admin only — gated on `users.impersonate`). Returns the
// same payload shape as login: a fresh Sanctum token scoped to the target user
// plus their feature flags. Stopping = POST /auth/logout with that token.
export const impersonateUser = async (id: string) => {
  const response = await api.post<ApiResponse<LoginSuccessResponse>>(`/users/${id}/impersonate`);
  return response.data.data;
};

// Get available roles
export const getRoles = async (): Promise<string[]> => {
  const response = await api.get<{ data: { id: number; name: string }[] }>('/roles');
  return response.data.data.map((r) => r.name);
};

// ---------------------------------------------------------------------------
// Signed-in devices (Radiance 260918). Self-service for everyone; anyone
// else's list needs users.edit — the API enforces it, the card only decides
// whether to render.
export interface UserSession {
  id: number
  /** "session" for a password/OTP login; "impersonation" for an admin acting as the user. */
  kind: string
  device: { os: string | null; browser: string | null; label: string }
  ip: string | null
  created_at: string | null
  last_used_at: string | null
  /** The token the viewer is using right now — only ever true on your own list. */
  current: boolean
}

export const getUserSessions = async (id: string) => {
  const response = await api.get<ApiResponse<UserSession[]>>(`/users/${id}/sessions`);
  return response.data.data;
};

export const revokeUserSession = async (id: string, sessionId: number) => {
  await api.delete(`/users/${id}/sessions/${sessionId}`);
};

/** Signs the user out on every device; the caller's own device survives when it is their account. */
export const revokeAllUserSessions = async (id: string) => {
  const response = await api.delete<ApiResponse<{ revoked: number }>>(`/users/${id}/sessions`);
  return response.data.data.revoked;
};
